import React from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { formatTime } from "@/lib/game/numberPlacement";
import { ArrowLeft, Lightbulb, RotateCcw, Shuffle, Clock, Calendar, Infinity as InfinityIcon } from "lucide-react";

export const GameControls = ({
  mode,
  difficulty,
  elapsed,
  placed,
  total,
  hintsUsed,
  canHint,
  onHint,
  onRestart,
  onNew,
}) => {
  const { t } = useApp();
  const navigate = useNavigate();

  const Badge = ({ children, testid }) => (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border-2 border-ink bg-white text-xs font-black font-display shadow-brutal-sm"
    >
      {children}
    </span>
  );

  const IconBtn = ({ testid, onClick, disabled, className, children, label }) => (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md border-2 border-ink font-display font-bold text-sm shadow-brutal-sm transition-transform enabled:hover:-translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed ${className}`}
    >
      {children}
    </button>
  );

  return (
    <div className="w-full max-w-3xl mx-auto flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <button
          data-testid="back-btn"
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border-2 border-ink bg-white font-display font-bold text-sm shadow-brutal-sm hover:-translate-y-0.5 transition-transform"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2.5} /> {t("back")}
        </button>

        <div className="flex items-center gap-2 flex-wrap justify-end">
          <Badge testid="mode-badge">
            {mode === "daily" ? (
              <><Calendar className="w-3.5 h-3.5" strokeWidth={2.5} /> {t("mode_daily")}</>
            ) : (
              <><InfinityIcon className="w-3.5 h-3.5" strokeWidth={2.5} /> {t("mode_endless")}</>
            )}
          </Badge>
          <Badge testid="difficulty-badge">{t(difficulty)}</Badge>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span
            data-testid="timer"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border-2 border-ink bg-ink text-cream font-mono font-bold text-sm shadow-brutal-sm tabular-nums"
          >
            <Clock className="w-4 h-4" strokeWidth={2.5} /> {formatTime(elapsed)}
          </span>
          <span data-testid="progress-count" className="text-sm font-bold font-display text-ink/70">
            {t("placed")}: {placed}/{total}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <IconBtn
            testid="hint-btn"
            onClick={onHint}
            disabled={!canHint}
            label={t("hint")}
            className="bg-sunny text-ink"
          >
            <Lightbulb className="w-4 h-4" strokeWidth={2.5} />
            <span className="hidden sm:inline">{t("hint")}</span>
            {hintsUsed > 0 && <span className="font-mono">({hintsUsed})</span>}
          </IconBtn>

          <IconBtn testid="restart-btn" onClick={onRestart} label={t("restart")} className="bg-white text-ink">
            <RotateCcw className="w-4 h-4" strokeWidth={2.5} />
            <span className="hidden sm:inline">{t("restart")}</span>
          </IconBtn>

          {onNew && (
            <IconBtn testid="new-puzzle-btn" onClick={onNew} label={t("new_puzzle")} className="bg-coral text-white">
              <Shuffle className="w-4 h-4" strokeWidth={2.5} />
              <span className="hidden sm:inline">{t("new_puzzle")}</span>
            </IconBtn>
          )}
        </div>
      </div>
    </div>
  );
};
